<template>
  <header class="header glass-effect" :class="{ 'scrolled': isScrolled }">
    <div class="container">
      <nav class="nav">
        <div class="logo">
          <img src="/images/z3c.png" alt="Z3C Logo" class="logo-img" />
        </div>
        
        <div class="nav-links">
          <a href="#home" class="nav-link">{{ $t('header.home') }}</a>
          <a href="#clients" class="nav-link">Clientes</a>
          <a href="#features" class="nav-link">{{ $t('header.services') }}</a>
          <a href="#contact" class="nav-link">{{ $t('header.contact') }}</a>
        </div>
        
        <LanguageSelector />
        
        <button
          type="button"
          class="nav-toggle"
          @click="toggleMobileMenu"
          :class="{ active: isMobileMenuOpen }"
          :aria-expanded="isMobileMenuOpen"
          aria-controls="mobile-menu"
          :aria-label="isMobileMenuOpen ? 'Fechar menu de navegação' : 'Abrir menu de navegação'"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
      </nav>
      
      <div id="mobile-menu" class="mobile-menu" :class="{ active: isMobileMenuOpen }">
        <a href="#home" class="mobile-nav-link" @click="closeMobileMenu">{{ $t('header.home') }}</a>
        <a href="#clients" class="mobile-nav-link" @click="closeMobileMenu">Clientes</a>
        <a href="#features" class="mobile-nav-link" @click="closeMobileMenu">{{ $t('header.services') }}</a>
        <a href="#contact" class="mobile-nav-link" @click="closeMobileMenu">{{ $t('header.contact') }}</a>
        <div class="mobile-language-selector">
          <LanguageSelector />
        </div>
      </div>
    </div>
  </header>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import LanguageSelector from './LanguageSelector.vue'

const isMobileMenuOpen = ref(false)
const isScrolled = ref(false)

const toggleMobileMenu = () => {
  isMobileMenuOpen.value = !isMobileMenuOpen.value
}

const closeMobileMenu = () => {
  isMobileMenuOpen.value = false
}

const handleScroll = () => {
  isScrolled.value = window.scrollY > 50
}

onMounted(() => {
  window.addEventListener('scroll', handleScroll)
})

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
})
</script>

<style scoped>
.header {
  padding: var(--space-6) 0;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-bottom: 1px solid var(--border-light);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: var(--shadow-sm);
}

.header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 1px;
  background: var(--gradient-primary);
  opacity: 0;
  transition: opacity 0.4s ease;
}

.header.scrolled::before {
  opacity: 1;
}

.header.scrolled {
  padding: var(--space-4) 0;
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(25px);
  -webkit-backdrop-filter: blur(25px);
  border-bottom: 1px solid var(--border-medium);
  box-shadow: var(--shadow-lg);
}

.nav {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.logo {
  display: flex;
  align-items: center;
  gap: 1rem;
  cursor: pointer;
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.logo:hover {
  transform: translateY(-2px);
}

.logo-img {
  width: 65px;
  height: 65px;
  object-fit: contain;
  filter: drop-shadow(0 4px 12px rgba(59, 130, 246, 0.3));
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  animation: logoFloat 6s ease-in-out infinite;
}

@keyframes logoFloat {
  0%, 100% {
    transform: translateY(0px) rotate(0deg);
    filter: drop-shadow(0 4px 12px rgba(59, 130, 246, 0.3));
  }
  50% {
    transform: translateY(-3px) rotate(1deg);
    filter: drop-shadow(0 8px 20px rgba(59, 130, 246, 0.4));
  }
}

/* Estilos do logo-text removidos */

.nav-links {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  background: rgba(248, 250, 252, 0.8);
  padding: var(--space-2);
  border-radius: var(--border-radius-full);
  border: 1px solid var(--border-light);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

.nav-link {
  color: var(--text-secondary);
  text-decoration: none;
  font-weight: var(--font-weight-semibold);
  font-size: var(--font-size-sm);
  position: relative;
  padding: var(--space-3) var(--space-4);
  border-radius: var(--border-radius-full);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  letter-spacing: 0.025em;
  white-space: nowrap;
}

.nav-link:hover {
  color: var(--primary-600);
  background: var(--primary-50);
  transform: translateY(-1px);
  box-shadow: var(--shadow-sm);
}

.nav-link:active {
  transform: translateY(0);
  background: var(--primary-100);
}

.nav-link::before {
  content: '';
  position: absolute;
  bottom: var(--space-1);
  left: 50%;
  width: 0;
  height: 2px;
  background: var(--gradient-primary);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  transform: translateX(-50%);
  border-radius: var(--border-radius-sm);
}

.nav-link:hover::before {
  width: 80%;
}

.nav-toggle {
  display: none;
  flex-direction: column;
  gap: 4px;
  cursor: pointer;
  padding: 0.5rem;
  background: none;
  border: none;
  font: inherit;
}

.nav-toggle span {
  width: 28px;
  height: 3px;
  background: #475569;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: 2px;
}

.nav-toggle:hover span {
  background: #1e293b;
}

.nav-toggle.active span:nth-child(1) {
  transform: rotate(45deg) translate(6px, 6px);
  background: #3b82f6;
}

.nav-toggle.active span:nth-child(2) {
  opacity: 0;
  transform: scale(0);
}

.nav-toggle.active span:nth-child(3) {
  transform: rotate(-45deg) translate(8px, -8px);
  background: #3b82f6;
}

.mobile-menu {
  display: none;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem 0;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  margin-top: 1rem;
}

.mobile-menu.active {
  display: flex;
}

.mobile-nav-link {
  color: #475569;
  text-decoration: none;
  font-weight: 600;
  padding: 1rem 0;
  font-size: 1.1rem;
  border-bottom: 1px solid rgba(226, 232, 240, 0.5);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.mobile-nav-link:hover {
  color: #1e293b;
  padding-left: 1rem;
  background: rgba(59, 130, 246, 0.05);
}

.mobile-nav-link::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  width: 3px;
  background: #3b82f6;
  transform: scaleY(0);
  transition: transform 0.3s ease;
  transform-origin: bottom;
}

.mobile-nav-link:hover::before {
  transform: scaleY(1);
}

.mobile-language-selector {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

@media (max-width: 768px) {
  .nav-links {
    display: none;
  }
  
  .nav-toggle {
    display: flex;
  }
  
  .logo-img {
    width: 55px;
    height: 55px;
  }
  
  .header {
    padding: 1rem 0;
  }
  
  .header.scrolled {
    padding: 0.75rem 0;
  }
}

@media (max-width: 1024px) {
  .nav-links {
    gap: var(--space-1);
    padding: var(--space-1);
  }
  
  .nav-link {
    padding: var(--space-2) var(--space-3);
    font-size: var(--font-size-xs);
  }
}

@media (max-width: 480px) {
  .logo-img {
    width: 50px;
    height: 50px;
  }
}

@media (min-width: 769px) {
  .mobile-language-selector {
    display: none;
  }
}
</style>

